import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Clock, Users, FileText, X, Save, Trash2, MapPin } from 'lucide-react';
// import Button from '../common/Button/Button';
import SearchableDropdown from '../common/SearchableDropdown';
import {
  handleNumberInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../utils/inputValidation';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../utils/formValidation';
import { toast } from '../../utils/toast';

/**
 * LocationSearchInput - Component for Google Maps Address Autocomplete with Custom Dropdown
 */
const LocationSearchInput = ({
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  maxLength,
  className,
  onGeometryChange
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null); // Ref for PlacesService
  const inputContainerRef = useRef(null);

  // Sync internal state with prop
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Initialize Services
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      // Initialize PlacesService with a dummy div
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
  }, []);

  const handleInputChange = (e) => {
    const rawVal = e.target.value || '';
    const val = rawVal.replace(INVALID_CHARS_REGEX, '');
    setInputValue(val);

    // Propagate change to parent
    if (onChange) {
      // Mock event object to match expected interface
      onChange({ target: { value: val } });
    }

    if (!val || val.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions({
        input: val,
        types: ['geocode', 'establishment']
      }, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      });
    }
  };

  const handlePredictionSelect = (prediction) => {
    setInputValue(prediction.description);
    setPredictions([]);
    setShowPredictions(false);

    // Propagate address selection to parent
    if (onChange) {
      onChange({ target: { value: prediction.description } });
    }

    // Explicitly fetch geometry details using place_id
    if (onGeometryChange && placesService.current && prediction.place_id) {
      placesService.current.getDetails({
        placeId: prediction.place_id,
        fields: ['geometry']
      }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          onGeometryChange({ lat, lng });
        }
      });
    }


    // Trigger onBlur validation if provided
    if (onBlur) {
      onBlur();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
        setShowPredictions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={inputContainerRef}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={() => {
          // Delay onBlur to allow click on suggestion to register first
          setTimeout(() => {
            if (onBlur) onBlur();
          }, 200);
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        className={className}
        autoComplete="off"
      />
      {window.google && window.google.maps && (
        <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      )}

      {/* Custom Suggestions Dropdown */}
      {showPredictions && predictions.length > 0 && (
        <div className="absolute z-50 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-start transition-colors"
              onClick={() => handlePredictionSelect(prediction)}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 mr-2 text-blue-500 flex-shrink-0" />
              <span>{prediction.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * RoutePlanFormModal - Reusable modal component for creating and editing route plans
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {'create' | 'edit'} props.mode - Mode of the modal ('create' or 'edit')
 * @param {Object} props.initialData - Initial data for edit mode (route plan object)
 * @param {Function} props.onSave - Callback function when save button is clicked, receives form data
 * @param {Array} props.repOptions - Options for sales rep dropdown
 * @param {boolean} props.loadingReps - Loading state for reps
 * @param {Function} props.onRepSearch - Callback for rep search
 * @param {Array} props.leadOptions - Options for leads dropdown
 * @param {boolean} props.loadingLeads - Loading state for leads
 * @param {Function} props.onLeadSearch - Callback for lead search
 * @param {Array} props.customerOptions - Options for customers dropdown
 * @param {boolean} props.loadingCustomers - Loading state for customers
 * @param {Function} props.onCustomerSearch - Callback for customer search
 * @param {Array} props.leads - Array of lead/customer objects (for matching)
 * @param {string} props.defaultDate - Default date value (for create mode)
 * @param {boolean} props.isSaving - Whether save operation is in progress
 */
const RoutePlanFormModal = ({
  isOpen,
  onClose,
  mode = 'create',
  initialData = null,
  onSave,
  repOptions = [],
  loadingReps = false,
  onRepSearch,
  leadOptions = [],
  loadingLeads = false,
  onLeadSearch,
  customerOptions = [],
  loadingCustomers = false,
  onCustomerSearch,
  leads = [],
  defaultDate = '',
  isSaving = false
}) => {
  // Get today's date for date validation
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Calculate minimum date for edit mode
  const minDate = useMemo(() => {
    if (mode === 'edit' && initialData && initialData.date) {
      // Parse the initial date from the route plan
      const initialDateStr = typeof initialData.date === 'string'
        ? initialData.date.split('T')[0]
        : new Date(initialData.date).toISOString().split('T')[0];

      const initialDate = new Date(initialDateStr);
      initialDate.setHours(0, 0, 0, 0);

      // If initial date is in the past, allow editing up to that date
      // If initial date is today or future, allow from today onwards
      if (initialDate < today) {
        return initialDateStr; // Return the initial date (already in past)
      }
    }
    // For create mode or if initial date is today or future, use today
    return todayStr;
  }, [mode, initialData, todayStr, today]);

  const [formData, setFormData] = useState({
    rep: '',
    date: defaultDate || todayStr,
    customers: [{ name: '', address: '', time: '', duration: 60, latitude: null, longitude: null, type: 'lead' }],
    notes: '',
    status: 'scheduled'
  });

  const [formErrors, setFormErrors] = useState({});

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        // Format date to YYYY-MM-DD
        const planDate = initialData.date
          ? (typeof initialData.date === 'string'
            ? initialData.date.split('T')[0]
            : new Date(initialData.date).toISOString().split('T')[0])
          : '';

        // Map customers from plan to form format
        const customers = (initialData.customers || []).map(customer => ({
          _id: customer.leadId?._id || customer.leadId || customer.customerId?._id || customer.customerId || null,
          name: customer.name || '',
          address: customer.address || '',
          time: customer.time || '',
          duration: customer.duration || 60,
          latitude: customer.latitude || null,
          longitude: customer.longitude || null,
          type: customer.leadId ? 'lead' : (customer.customerId ? 'customer' : 'lead')
        }));

        // If no customers, add one empty customer
        if (customers.length === 0) {
          customers.push({ name: '', address: '', time: '', duration: 60, latitude: null, longitude: null, type: 'lead' });
        }

        setFormData({
          rep: initialData.rep?._id || initialData.rep || '', // Handle object or ID
          date: planDate,
          customers: customers,
          notes: initialData.notes || '',
          status: initialData.status || 'scheduled'
        });
      } else {
        // Reset for create mode
        setFormData({
          rep: '',
          date: defaultDate || todayStr,
          customers: [{ name: '', address: '', time: '', duration: 60, latitude: null, longitude: null, type: 'lead' }],
          notes: '',
          status: 'scheduled'
        });
      }
      setFormErrors({});
    }
  }, [isOpen, mode, initialData, defaultDate, todayStr]);

  const handleCustomerChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customers: prev.customers.map((customer, i) =>
        i === index ? { ...customer, [field]: value } : customer
      )
    }));
  };

  // Helper to update multiple fields for a customer (e.g. lat/lng)
  const handleCustomerFieldsChange = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      customers: prev.customers.map((customer, i) =>
        i === index ? { ...customer, ...updates } : customer
      )
    }));
  };

  const handleCustomerNameChange = (index, e) => {
    const rawValue = e.target.value || '';
    const sanitizedValue = rawValue.replace(INVALID_CHARS_REGEX, '').slice(0, FIELD_LIMITS.customerName);
    handleCustomerChange(index, 'name', sanitizedValue);
    if (formErrors[`customer_${index}_name`]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`customer_${index}_name`];
        return newErrors;
      });
    }
  };

  const handleCustomerAddressChange = (index, e) => {
    // Check if input is from Google Autocomplete object or standard event
    const value = e && e.target ? e.target.value : e;
    const sanitized = String(value || '').replace(INVALID_CHARS_REGEX, '');
    const limitedValue = sanitized.slice(0, FIELD_LIMITS.address);

    handleCustomerChange(index, 'address', limitedValue);

    // If user is typing manually, clear lat/lng to avoid stale data unless explicitly selecting
    if (!e || (e.target && e.target.value)) {
      // Only clear if manual edit - LocationSearchInput handles clearing predictions on edit
      // But here we can't distinguish easily without more state. 
      // For now, let's keep old lat/lng if manual edit, assuming they correct a typo.
      // Or explicitly clear if empty.
      if (!limitedValue) {
        handleCustomerFieldsChange(index, { latitude: null, longitude: null });
      }
    }

    if (formErrors[`customer_${index}_address`]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`customer_${index}_address`];
        return newErrors;
      });
    }
  };

  const handleGeometryChange = (index, { lat, lng }) => {
    handleCustomerFieldsChange(index, { latitude: lat, longitude: lng });
  };

  // Handle blur event for validation
  const handleBlur = (field, index = null) => {
    let error = '';

    // Global fields
    if (field === 'rep') {
      if (!formData.rep) error = 'Assigned To is required';
    }
    else if (field === 'date') {
      if (!formData.date) error = 'Visit Date is required';
      else if (mode === 'create' && formData.date < todayStr) {
        error = 'Date cannot be in the past.';
      }
    }

    // Customer specific fields
    else if (index !== null) {
      if (field === 'name') {
        if (!formData.customers[index].name?.trim()) error = 'Customer name is required';
      }
      else if (field === 'address') {
        if (!formData.customers[index].address?.trim()) error = 'Address is required';
      }
      else if (field === 'time') {
        if (!formData.customers[index].time) error = 'Visit time is required';
      }
      else if (field === 'duration') {
        if (!formData.customers[index].duration) error = 'Duration is required';
      }
    }

    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [index !== null ? `customer_${index}_${field}` : field]: error
      }));
    }
  };

  const handleCustomerDurationChange = (index, e) => {
    const numValue = handleNumberInputChange(e, null, NUMBER_LIMITS.duration.max, NUMBER_LIMITS.duration.min);
    handleCustomerChange(index, 'duration', numValue === '' ? 0 : numValue);
    if (formErrors[`customer_${index}_duration`]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`customer_${index}_duration`];
        return newErrors;
      });
    }
  };

  const handleRemoveCustomer = (index) => {
    // Prevent removing the last customer
    if (formData.customers.length <= 1) return;

    setFormData(prev => ({
      ...prev,
      customers: prev.customers.filter((_, i) => i !== index)
    }));

    // Clean up errors for removed customer
    setFormErrors(prev => {
      const newErrors = {};
      Object.keys(prev).forEach(key => {
        if (!key.startsWith(`customer_${index}`)) {
          // For customers coming AFTER the removed one, we'd theoretically need to re-index errors
          // But simply clearing relevant errors is safer/easier for now
          if (!key.startsWith('customer_')) {
            newErrors[key] = prev[key];
          }
        }
      });
      return newErrors;
    });
  };

  const handleAddCustomer = () => {
    setFormData(prev => ({
      ...prev,
      customers: [...prev.customers, { name: '', address: '', time: '', duration: 60, latitude: null, longitude: null, type: 'lead' }]
    }));
  };

  const handleSave = () => {
    // Clear previous errors
    setFormErrors({});

    // Validate required fields
    const requiredFields = ['rep', 'date'];
    const validation = validateRequiredFields(formData, requiredFields);

    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    // Validate date based on mode
    if (formData.date) {
      if (mode === 'create') {
        // For create mode, don't allow past dates
        const today = new Date().toISOString().split('T')[0];
        if (formData.date < today) {
          setFormErrors(prev => ({ ...prev, date: 'Date cannot be in the past. Please select today or a future date.' }));
          return;
        }
      } else {
        // For edit mode, validate against minDate
        if (formData.date < minDate) {
          setFormErrors(prev => ({
            ...prev,
            date: minDate < todayStr
              ? `Date cannot be earlier than ${minDate}. You can only edit up to the original date.`
              : 'Date cannot be in the past. Please select today or a future date.'
          }));
          return;
        }
      }
    }

    // Validate customers array
    if (formData.customers.length === 0) {
      setFormErrors(prev => ({ ...prev, customers: 'Please add at least one customer.' }));
      return;
    }

    // Validate customer data
    const customerErrors = {};
    let hasCustomerErrors = false;
    let firstErrorField = null;

    formData.customers.forEach((customer, index) => {
      if (!customer.name || customer.name.trim() === '') {
        const errorKey = `customer_${index}_name`;
        customerErrors[errorKey] = 'Customer name is required';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      } else if (!VALID_TEXT_REGEX.test(customer.name)) {
        const errorKey = `customer_${index}_name`;
        customerErrors[errorKey] = 'Customer name contains invalid characters';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      }

      if (!customer.address || customer.address.trim() === '') {
        const errorKey = `customer_${index}_address`;
        customerErrors[errorKey] = 'Address is required';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      } else if (!VALID_TEXT_REGEX.test(customer.address)) {
        const errorKey = `customer_${index}_address`;
        customerErrors[errorKey] = 'Address contains invalid characters';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      }

      if (!customer.time || customer.time.trim() === '') {
        const errorKey = `customer_${index}_time`;
        customerErrors[errorKey] = 'Time is required';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      }

      if (!customer.duration || customer.duration < 0) {
        const errorKey = `customer_${index}_duration`;
        customerErrors[errorKey] = 'Duration must be a positive number';
        hasCustomerErrors = true;
        if (!firstErrorField) firstErrorField = errorKey;
      }
    });

    if (formData.notes && !VALID_TEXT_REGEX.test(formData.notes)) {
      setFormErrors(prev => ({ ...prev, notes: 'Notes contain invalid characters.' }));
      return;
    }

    if (hasCustomerErrors) {
      setFormErrors(customerErrors);

      // Attempt to focus the first error field
      // We need to wait for render
      setTimeout(() => {
        const errorInputs = document.querySelectorAll('.border-red-500');
        if (errorInputs.length > 0) {
          errorInputs[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorInputs[0].focus();
        }
      }, 100);

      return;
    }

    // Call onSave callback with form data
    if (onSave) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      rep: '',
      date: defaultDate || todayStr,
      customers: [{ name: '', address: '', time: '', duration: 60, latitude: null, longitude: null, type: 'lead' }],
      notes: '',
      status: 'scheduled'
    });
    setFormErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Enhanced Header - Fixed at top */}
        <div className="sticky top-0 bg-white z-10 p-6 rounded-t-xl border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-light text-brand rounded-lg">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{mode === 'edit' ? 'Edit Route Plan' : 'Create Route Plan'}</h3>
                <p className="text-gray-500 text-sm mt-0.5">
                  {mode === 'edit' ? 'Update your sales route plan' : 'Plan your sales route for the selected date'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area - Only this part scrolls */}
        <div className="overflow-y-auto flex-grow">
          {/* Enhanced Form Content */}
          <div className="p-6 space-y-6">
            {/* Sales Rep and Date Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                  <Users className="h-3.5 w-3.5 mr-2 text-blue-500" />
                  Assigned To <span className="text-red-500 ml-0.5">*</span>
                </label>
                <SearchableDropdown
                  options={repOptions}
                  value={formData.rep || null}
                  onChange={(selectedId) => {
                    setFormData(prev => ({ ...prev, rep: selectedId || '' }));
                    if (formErrors.rep) {
                      setFormErrors(prev => ({ ...prev, rep: '' }));
                    }
                  }}
                  onSearch={onRepSearch}
                  placeholder={loadingReps ? 'Loading...' : 'Select Sales Rep'}
                  loading={loadingReps}
                  error={formErrors.rep}
                  onBlur={() => handleBlur('rep')}
                  searchPlaceholder="Search by name or email..."
                  minSearchLength={2}
                  debounceMs={300}
                  className={`input-field w-full ${formErrors.rep ? 'border-red-500' : ''}`}
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                  <Calendar className="h-3.5 w-3.5 mr-2 text-blue-500" />
                  Visit Date <span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    value={formData.date}
                    min={minDate}
                    onChange={(e) => {
                      const selectedDate = e.target.value;

                      // Validate date based on mode
                      if (mode === 'create') {
                        // For create mode, don't allow past dates
                        const today = new Date().toISOString().split('T')[0];
                        if (selectedDate < today) {
                          setFormErrors(prev => ({ ...prev, date: 'Date cannot be in the past. Please select today or a future date.' }));
                          return;
                        }
                      } else {
                        // For edit mode, validate against minDate (which is either today or the initial past date)
                        if (selectedDate < minDate) {
                          setFormErrors(prev => ({
                            ...prev,
                            date: minDate < todayStr
                              ? `Date cannot be earlier than ${minDate}. You can only edit up to the original date.`
                              : 'Date cannot be in the past. Please select today or a future date.'
                          }));
                          return;
                        }
                      }

                      setFormData(prev => ({ ...prev, date: selectedDate }));
                      if (formErrors.date) {
                        setFormErrors(prev => ({ ...prev, date: '' }));
                      }
                    }}
                    onBlur={() => handleBlur('date')}
                    className={`input-field w-full ${formErrors.date ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                </div>
                {formErrors.date && (
                  <p className="mt-1 text-xs text-red-600 font-medium">
                    {formErrors.date}
                  </p>
                )}
              </div>
            </div>

            {/* Status Section - Only show when editing */}
            {mode === 'edit' && (
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                  <Clock className="h-3.5 w-3.5 mr-2 text-blue-500" />
                  Status
                </label>
                <select
                  value={formData.status || 'scheduled'}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, status: e.target.value }));
                  }}
                  className="input-field w-full"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}

            {/* Customers Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <Users className="h-3.5 w-3.5 mr-2 text-blue-500" />
                  Customer Details
                </label>
                {/* Optional: Add Customer Button */}
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddCustomer}
                  className="text-xs py-1 h-8"
                >
                  + Add Another Customer
                </Button> */}
              </div>

              <div className="space-y-4">
                {formData.customers.map((customer, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-5 bg-gray-50 hover:border-brand-light transition-colors duration-200 relative group">
                    {formData.customers.length > 1 && (
                      <button
                        onClick={() => handleRemoveCustomer(index)}
                        className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                        title="Remove Customer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}

                    <div className="space-y-4">
                      {/* Type Selection */}
                      <div className="flex items-center space-x-6">
                        <label className={`flex items-center cursor-pointer group ${mode === 'edit' ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <input
                            type="radio"
                            name={`customer-type-${index}`}
                            className="w-4 h-4 text-brand border-gray-300 focus:ring-brand"
                            checked={customer.type === 'lead'}
                            disabled={mode === 'edit'}
                            onChange={() => {
                              if (mode === 'edit') return;
                              const newCustomers = [...formData.customers];
                              newCustomers[index].type = 'lead';
                              newCustomers[index].name = ''; // Clear selection on type change
                              newCustomers[index].address = '';
                              newCustomers[index]._id = null;
                              setFormData({ ...formData, customers: newCustomers });
                            }}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-900">Lead</span>
                        </label>
                        <label className={`flex items-center cursor-pointer group ${mode === 'edit' ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <input
                            type="radio"
                            name={`customer-type-${index}`}
                            className="w-4 h-4 text-brand border-gray-300 focus:ring-brand"
                            checked={customer.type === 'customer'}
                            disabled={mode === 'edit'}
                            onChange={() => {
                              if (mode === 'edit') return;
                              const newCustomers = [...formData.customers];
                              newCustomers[index].type = 'customer';
                              newCustomers[index].name = ''; // Clear selection on type change
                              newCustomers[index].address = '';
                              newCustomers[index]._id = null;
                              setFormData({ ...formData, customers: newCustomers });
                            }}
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-900">Customer</span>
                        </label>
                      </div>

                      {/* Customer Name Selection */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          {customer.type === 'customer' ? 'Select Customer' : 'Select Lead'} <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                          options={customer.type === 'customer' ? customerOptions : leadOptions}
                          value={customer._id || null}
                          disabled={mode === 'edit'}
                          onChange={(selectedId) => {
                            if (mode === 'edit') return;
                            const options = customer.type === 'customer' ? customerOptions : leadOptions;
                            const selectedOpt = options.find(opt => opt.id === selectedId);

                            if (selectedOpt) {
                              const newCustomers = [...formData.customers];
                              newCustomers[index] = {
                                ...newCustomers[index],
                                _id: selectedId,
                                name: selectedOpt.title,
                                address: selectedOpt.raw.address || '',
                                latitude: selectedOpt.raw.latitude || null,
                                longitude: selectedOpt.raw.longitude || null
                              };
                              setFormData({ ...formData, customers: newCustomers });

                              // Clear errors
                              setFormErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`customer_${index}_name`];
                                delete newErrors[`customer_${index}_address`];
                                return newErrors;
                              });
                            } else {
                              const newCustomers = [...formData.customers];
                              newCustomers[index] = {
                                ...newCustomers[index],
                                _id: null,
                                name: '',
                                address: '',
                                latitude: null,
                                longitude: null
                              };
                              setFormData({ ...formData, customers: newCustomers });
                            }
                          }}
                          onSearch={(searchTerm) => {
                            if (mode === 'edit') return;
                            if (customer.type === 'customer') {
                              onCustomerSearch(searchTerm);
                            } else {
                              onLeadSearch(searchTerm);
                            }
                          }}
                          placeholder={customer.type === 'customer' ? (loadingCustomers ? 'Loading customers...' : 'Select Customer') : (loadingLeads ? 'Loading leads...' : 'Select Lead')}
                          loading={customer.type === 'customer' ? loadingCustomers : loadingLeads}
                          error={formErrors[`customer_${index}_name`]}
                          onBlur={() => handleBlur('name', index)}
                          searchPlaceholder={customer.type === 'customer' ? "Search by ID, name, email, phone..." : "Search by ID, name, email, phone..."}
                          minSearchLength={2}
                          debounceMs={300}
                          className={`input-field w-full ${formErrors[`customer_${index}_name`] ? 'border-red-500' : ''}`}
                        />

                        {/* Manual Input Fallback */}
                        {customer.name && !(customer.type === 'customer' ? customerOptions : leadOptions).find(opt => opt.title === customer.name) && (
                          <div className="mt-2 text-sm text-gray-600">
                            Searching for: <span className="font-semibold">{customer.name}</span>
                          </div>
                        )}
                      </div>
                      {/* Duplicate error for manual input was here, handled by dropdown error or manual input error implicitly if managed right */}
                    </div>

                    {/* Address with Google Maps Autocomplete */}
                    <div className='mt-4'>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Address <span className="text-red-500">*</span></label>
                      <LocationSearchInput
                        value={customer.address || ''}
                        onChange={(e) => handleCustomerAddressChange(index, e)}
                        onGeometryChange={(geometry) => handleGeometryChange(index, geometry)}
                        onBlur={() => handleBlur('address', index)}
                        placeholder="Enter location or select from Google Maps..."
                        maxLength={FIELD_LIMITS.address}
                        className={`input-field w-full ${formErrors[`customer_${index}_address`] ? 'border-red-500 bg-red-50' : ''}`}
                      />
                      <div className="flex justify-between items-start mt-1">
                        <div className="flex-1">
                          {formErrors[`customer_${index}_address`] && (
                            <p className="text-xs text-red-600 font-medium">{formErrors[`customer_${index}_address`]}</p>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {getCharacterCount(customer.address || '', FIELD_LIMITS.address)}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Visit Time <span className="text-red-500">*</span></label>
                        <input
                          type="time"
                          onClick={(e) => e.target.showPicker && e.target.showPicker()}
                          value={customer.time}
                          onChange={(e) => {
                            handleCustomerChange(index, 'time', e.target.value);
                            // Clear error immediately
                            if (formErrors[`customer_${index}_time`]) {
                              setFormErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`customer_${index}_time`];
                                return newErrors;
                              });
                            }
                          }}
                          onBlur={() => handleBlur('time', index)}
                          className={`input-field w-full ${formErrors[`customer_${index}_time`] ? 'border-red-500 bg-red-50' : ''}`}
                        />
                        {formErrors[`customer_${index}_time`] && (
                          <p className="mt-1 text-xs text-red-600 font-medium">{formErrors[`customer_${index}_time`]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Duration (Minutes) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          placeholder="60"
                          value={customer.duration || ''}
                          onChange={(e) => handleCustomerDurationChange(index, e)}
                          onBlur={() => handleBlur('duration', index)}
                          min={NUMBER_LIMITS.duration.min}
                          max={NUMBER_LIMITS.duration.max}
                          className={`input-field w-full ${formErrors[`customer_${index}_duration`] ? 'border-red-500 bg-red-50' : ''}`}
                        />
                        {formErrors[`customer_${index}_duration`] && (
                          <p className="mt-1 text-xs text-red-600 font-medium">{formErrors[`customer_${index}_duration`]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5">
                <FileText className="h-3.5 w-3.5 mr-2 text-blue-500" />
                Additional Notes
              </label>
              <div>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => {
                    const sanitizedValue = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, FIELD_LIMITS.notes);
                    setFormData(prev => ({ ...prev, notes: sanitizedValue }));
                  }}
                  maxLength={FIELD_LIMITS.notes}
                  className="input-field w-full h-24 resize-none"
                  placeholder="Enter any additional notes or instructions..."
                />
                <div className="text-right mt-1 text-xs text-gray-400">
                  {getCharacterCount(formData.notes || '', FIELD_LIMITS.notes)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Footer - Fixed at bottom */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
          <button
            className="btn-cancel px-6"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-create px-6 flex items-center justify-center space-x-2 shadow-lg shadow-brand/30"
          >
            {isSaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <Save className="h-4 w-4 mr-2" />}
            <span>{mode === 'edit' ? 'Update Plan' : 'Save Plan'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutePlanFormModal;
